import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { FileUp, Loader2, Copy, MessageSquare, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export const REQUEST_DOCS = [
  { key: 'cni', label: "Carte Nationale d'Identité (CNI)" },
  { key: 'avis_impots', label: "Avis d'imposition" },
  { key: 'taxe_fonciere', label: 'Taxe foncière' },
  { key: 'carte_invalidite', label: "Carte d'invalidité (si handicap)" },
];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '');

export function useDocRequest(id) {
  const [req, setReq] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get(`/cee/dossiers/${id}/doc-request`); setReq(r.data.request); }
    catch { setReq(null); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  return [req, load];
}

function DocRequestModal({ open, onOpenChange, doc, id, onSent }) {
  const [email, setEmail] = useState('');
  const [selected, setSelected] = useState(REQUEST_DOCS.map((d) => d.key));
  const [extra, setExtra] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setEmail(doc?.beneficiaire?.email || '');
      setSelected(REQUEST_DOCS.map((d) => d.key));
      setExtra(''); setMessage('');
    }
  }, [open, doc]);

  const toggle = (k) => setSelected((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const extraList = extra.split('\n').map((s) => s.trim()).filter(Boolean);

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/cee/dossiers/${id}/doc-request`, {
        email: email.trim() || undefined, documents: selected,
        extra_documents: extraList, message: message.trim() || undefined,
      });
      if (r.data.email_sent) toast.success(`Demande envoyée à ${r.data.sent_to}`);
      else toast.warning("Lien créé mais l'email n'est pas parti — copiez le lien");
      onOpenChange(false); onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="doc-request-modal">
        <DialogHeader>
          <DialogTitle>Demander des documents au bénéficiaire</DialogTitle>
          <DialogDescription>Le bénéficiaire reçoit un lien (valable 30 jours) pour déposer ses documents, voir le compte rendu et laisser un commentaire.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Email du bénéficiaire</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="doc-request-email" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Documents à demander</label>
            <div className="space-y-1.5">
              {REQUEST_DOCS.map((d) => (
                <label key={d.key} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${selected.includes(d.key) ? 'border-[#004AAD] bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}
                       data-testid={`doc-request-doc-${d.key}`}>
                  <input type="checkbox" checked={selected.includes(d.key)} onChange={() => toggle(d.key)} className="h-4 w-4 accent-[#004AAD]" />
                  <span className="text-sm text-slate-800">{d.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Documents complémentaires (un par ligne)</label>
            <Textarea rows={3} value={extra} onChange={(e) => setExtra(e.target.value)}
                      placeholder={'Justificatif de domicile\nAttestation CAF'} data-testid="doc-request-extra" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Message (optionnel)</label>
            <Textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="doc-request-message" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="doc-request-cancel-btn">Annuler</Button>
            <Button onClick={send} disabled={sending || (!selected.length && !extraList.length)}
                    className="bg-[#004AAD] hover:bg-[#003580]" data-testid="doc-request-send-btn">
              {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Envoyer la demande
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DocRequestPanel({ doc, id }) {
  const [req, reload] = useDocRequest(id);
  const [open, setOpen] = useState(false);
  const link = req?.token ? `${window.location.origin}/depot/${req.token}` : '';
  return (
    <Card className="p-5 border-slate-200 space-y-3" data-testid="doc-request-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-bold text-slate-900">Demande de documents au bénéficiaire</h2>
          <p className="text-xs text-slate-500">Envoie un email avec un espace de dépôt, l'aperçu du compte rendu et un champ commentaire.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#004AAD] hover:bg-[#003580]" data-testid="doc-request-open-btn">
          <FileUp className="w-4 h-4 mr-2" />{req ? 'Renvoyer une demande' : 'Demander des documents'}
        </Button>
      </div>
      {req && (
        <div className="text-xs text-slate-600 bg-slate-50 rounded-lg p-3 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="doc-request-status">
          <span>Envoyée le {fmt(req.sent_at)} à <b>{req.sent_to}</b></span>
          <span className="flex items-center gap-1"><Paperclip className="w-3 h-3" />{req.files_count} fichier(s) reçu(s)</span>
          <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{req.comments.length} commentaire(s)</span>
          <span>Expire le {fmt(req.expires_at)}</span>
          <button className="text-[#004AAD] font-medium flex items-center gap-1 hover:underline" data-testid="doc-request-copy-link"
                  onClick={() => { navigator.clipboard.writeText(link); toast.success('Lien copié'); }}>
            <Copy className="w-3 h-3" />Copier le lien
          </button>
        </div>
      )}
      <DocRequestModal open={open} onOpenChange={setOpen} doc={doc} id={id} onSent={reload} />
    </Card>
  );
}

export function DocRequestFiles({ id }) {
  const [req] = useDocRequest(id);
  if (!req) return null;
  const base = process.env.REACT_APP_BACKEND_URL;
  return (
    <div className="pt-4 border-t border-slate-100 space-y-3" data-testid="doc-request-files">
      <h3 className="font-semibold text-slate-900 text-sm">Documents déposés par le bénéficiaire</h3>
      {req.items.map((it) => (
        <div key={it.key} className="border border-slate-100 rounded-lg p-3">
          <div className="text-sm font-medium text-slate-900">{it.label}{it.extra && <span className="text-xs text-slate-400"> · complémentaire</span>}</div>
          {it.files.length === 0 ? (
            <div className="text-xs text-slate-400 italic mt-1">En attente</div>
          ) : it.files.map((f) => (
            <a key={f.file_id} href={`${base}${f.url}`} target="_blank" rel="noreferrer"
               className="text-xs text-[#004AAD] hover:underline flex items-center gap-1 mt-1" data-testid={`doc-request-file-${f.file_id}`}>
              <Paperclip className="w-3 h-3" />{f.filename} <span className="text-slate-400">· {fmt(f.uploaded_at)}</span>
            </a>
          ))}
        </div>
      ))}
      {req.comments.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold text-slate-600">Commentaires du bénéficiaire</div>
          {req.comments.map((c, i) => (
            <div key={i} className="text-sm bg-blue-50 border border-blue-100 rounded-lg p-3 whitespace-pre-line" data-testid={`doc-request-comment-${i}`}>
              {c.text}<div className="text-[11px] text-slate-400 mt-1">{fmt(c.at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
